import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import { acknowledgeNotification, dismissNotification, fetchActiveNotifications } from '../api';
import { notificationMatchesRoute } from '../matchRoute';
import { Notification } from '../types';

const ACTIVE_NOTIFICATIONS_STALE_TIME_MS = 5 * 60 * 1000;

function activeNotificationsQueryKey(userId: number | null | undefined) {
  return ['notifications', 'active', userId ?? null] as const;
}

function sortNotifications(notifications: Notification[]): Notification[] {
  return [...notifications].sort((a, b) => {
    if (a.priority !== b.priority) {
      return b.priority - a.priority;
    }
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

/**
 * Loads every active notification once and keeps that list for 5 minutes.
 * Navigation filters the cached list locally. The list is refetched on that
 * interval (and when the window regains focus after it has gone stale) so new
 * notifications appear and admin-deleted ones drop off.
 */
export function useNotifications() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuth();
  const currentRoute = location.pathname;
  const queryKey = useMemo(() => activeNotificationsQueryKey(user?.user_id), [user?.user_id]);

  const { data: allNotifications = [], isLoading } = useQuery({
    queryKey,
    queryFn: fetchActiveNotifications,
    enabled: isAuthenticated,
    staleTime: ACTIVE_NOTIFICATIONS_STALE_TIME_MS,
    refetchInterval: ACTIVE_NOTIFICATIONS_STALE_TIME_MS,
    refetchOnWindowFocus: true
  });

  const [closedForRoute, setClosedForRoute] = useState<{ route: string; ids: ReadonlySet<string> }>({
    route: currentRoute,
    ids: new Set()
  });
  if (closedForRoute.route !== currentRoute) {
    setClosedForRoute({ route: currentRoute, ids: new Set() });
  }
  const closedIds = closedForRoute.route === currentRoute ? closedForRoute.ids : new Set<string>();

  const notifications = useMemo(
    () =>
      sortNotifications(
        allNotifications.filter(
          (notification) =>
            notificationMatchesRoute(notification.route, currentRoute) && !closedIds.has(notification.id)
        )
      ),
    [allNotifications, closedIds, currentRoute]
  );

  const removeFromCache = useCallback(
    (notificationId: string) => {
      queryClient.setQueryData<Notification[]>(queryKey, (current) =>
        current?.filter((notification) => notification.id !== notificationId)
      );
    },
    [queryClient, queryKey]
  );

  const dismiss = useCallback(
    async (notificationId: string) => {
      removeFromCache(notificationId);
      try {
        await dismissNotification(notificationId);
      } catch (error) {
        console.error('Failed to dismiss notification:', error);
      }
    },
    [removeFromCache]
  );

  const neverShow = useCallback(
    async (notificationId: string) => {
      removeFromCache(notificationId);
      try {
        await acknowledgeNotification(notificationId);
      } catch (error) {
        console.error('Failed to acknowledge notification:', error);
      }
    },
    [removeFromCache]
  );

  const close = useCallback((notificationId: string) => {
    setClosedForRoute((current) => {
      const ids = new Set(current.ids);
      ids.add(notificationId);
      return { route: current.route, ids };
    });
  }, []);

  const acknowledge = useCallback(
    async (notificationId: string) => {
      removeFromCache(notificationId);
      try {
        await acknowledgeNotification(notificationId);
      } catch (error) {
        console.error('Failed to acknowledge notification:', error);
      }
    },
    [removeFromCache]
  );

  return {
    notifications,
    loading: isLoading,
    close,
    dismiss,
    neverShow,
    acknowledge
  };
}
